import { useEffect, useMemo, useRef } from "react";
import { useStationStatus } from "@/hooks/useStationStatus";
import { useLiveAudio } from "@/hooks/useLiveAudio";
import { LedVisualizer } from "@/components/listen/LedVisualizer";
import { CarDeck } from "@/components/listen/CarDeck";
import { PioneerDeck } from "@/components/listen/PioneerDeck";
import { useVolume } from "@/hooks/useVolume";
import { BottomBar } from "@/components/listen/BottomBar";
import { RecentList } from "@/components/listen/RecentList";
import { StreamLinks } from "@/components/listen/StreamLinks";
import { API } from "@/lib/api";
import { fadeGradient, photoMask, themeVars, vizOptions } from "@/lib/theme";
import { VideoSection } from "@/components/listen/VideoSection";
import { BeatClock } from "@/lib/beatClock";

export default function Listen() {
  const { status, elapsed, syncAt } = useStationStatus();
  const audioRef = useRef(null);
  const audio = useLiveAudio(audioRef);
  const vol = useVolume(audioRef);
  const clock = useMemo(() => new BeatClock(), []);
  const st = status?.station;
  useEffect(() => { clock.setStatus(status?.now_playing, syncAt); }, [clock, status, syncAt]);
  const Player = st?.theme?.player_style === "pioneer" ? PioneerDeck : CarDeck;

  return (
    <div className="listen-theme dot-grid relative min-h-screen overflow-hidden pb-28" style={themeVars(st?.theme)} data-testid="listen-page">
      <audio ref={audioRef} preload="none" />
      {st?.has_background && (
        <div className="absolute inset-x-0 top-0 h-[75vh]" data-testid="listen-background">
          <img src={`${API}/station/background?v=${st.background_version}`} alt="" className="w-full h-full object-cover" style={{ opacity: "var(--t-img-opacity)", ...photoMask(st.theme?.fade_strength ?? 80) }} />
          <div className="absolute inset-0" style={{ background: fadeGradient(st.theme?.fade_strength ?? 80) }} />
        </div>
      )}
      <main className="relative max-w-6xl mx-auto px-3 sm:px-5 flex flex-col gap-12">
        <div className="pt-6">
          <h1 className="sr-only" data-testid="station-name">{st?.name || "Lowride Life Radio"}</h1>
          <LedVisualizer clock={clock} audioApi={audio} text={st?.name || "Lowride Life Radio"} font={st?.theme?.display_font || "Bungee"} playing={audio.playing} opts={vizOptions(st?.theme)}
            imageUrl={st?.has_name_image ? `${API}/station/name_image?v=${st.name_image_version}` : null}
            className={`w-full ${st?.theme?.viz_name === false ? "h-[160px] sm:h-[220px]" : "h-[260px] sm:h-[380px] lg:h-[440px]"}`} />
          <p className="text-center text-sm mt-3" style={{ color: "var(--t-muted)" }} data-testid="station-tagline">{st?.tagline}</p>
        </div>
        <Player status={status} elapsed={elapsed} audio={audio} clock={clock} vol={vol} />
        <VideoSection loopDefault={st?.video_loop ?? true} faceplate={st?.theme?.video_faceplate} />
        <RecentList history={status?.history} faceplate={st?.theme?.deck_faceplate} />
        <StreamLinks />
      </main>
      <BottomBar status={status} audio={audio} vol={vol} />
    </div>
  );
}
